import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Users, 
  Save, 
  RefreshCw,
  Settings,
  MessageCircle,
  Image,
  Link,
  Bell,
  Shield,
  ExternalLink,
  Copy,
  BarChart3,
  TrendingUp,
  UserPlus,
  UserMinus
} from 'lucide-react';
import './Group.css';

function Group() {
  const [settings, setSettings] = useState({
    group_id: '',
    group_username: '',
    group_title: '',
    group_description: '',
    invite_link: '',
    photo_url: '',
    auto_approve: false,
    welcome_enabled: true,
    welcome_message: '',
    rules_enabled: false,
    rules_text: '',
    anti_spam: true,
    anti_flood: true,
    flood_limit: 5,
    flood_timeout: 60,
    notifications_enabled: true
  });
  const [stats, setStats] = useState({
    members_count: 0,
    messages_today: 0,
    new_members_today: 0,
    left_members_today: 0,
    active_users: 0,
    growth_percent: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes] = await Promise.all([
        api.get('/api/admin/group/settings').catch(() => ({ data: {} })),
        api.get('/api/admin/group/stats').catch(() => ({ data: {} }))
      ]);

      if (settingsRes.data.settings) {
        setSettings(prev => ({ ...prev, ...settingsRes.data.settings }));
      }
      if (statsRes.data.stats) {
        setStats(prev => ({ ...prev, ...statsRes.data.stats }));
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/group/settings', settings);
      alert('Настройки сохранены!');
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      alert('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const copyInviteLink = () => {
    if (settings.invite_link) {
      navigator.clipboard.writeText(settings.invite_link);
      alert('Ссылка скопирована!');
    }
  };

  const tabs = [
    { id: 'general', label: 'Основные', icon: Settings },
    { id: 'moderation', label: 'Модерация', icon: Shield },
    { id: 'welcome', label: 'Приветствие', icon: MessageCircle }
  ];

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="group-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Настройки группы</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadData}>
            <RefreshCw size={18} />
            Обновить
          </button>
          <button 
            className="btn btn-primary" 
            onClick={saveSettings}
            disabled={saving}
          >
            <Save size={18} />
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon members">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.members_count.toLocaleString()}</span>
            <span className="stat-label">Участников</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon messages">
            <MessageCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.messages_today}</span>
            <span className="stat-label">Сообщений сегодня</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon new-members">
            <UserPlus size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">+{stats.new_members_today}</span>
            <span className="stat-label">Новых сегодня</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon growth">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.growth_percent > 0 ? '+' : ''}{stats.growth_percent}%</span>
            <span className="stat-label">Рост за неделю</span>
          </div>
        </div>
      </div>

      {/* Group Info Card */}
      <div className="group-info-card">
        <div className="group-photo">
          {settings.photo_url ? (
            <img src={settings.photo_url} alt={settings.group_title} />
          ) : (
            <div className="photo-placeholder">
              <Users size={32} />
            </div>
          )}
        </div>
        <div className="group-details">
          <h2 className="group-title">{settings.group_title || 'Название группы'}</h2>
          <span className="group-username">@{settings.group_username || 'username'}</span>
          <p className="group-description">{settings.group_description || 'Описание группы'}</p>
        </div>
        {settings.invite_link && (
          <div className="invite-section">
            <div className="invite-link">
              <Link size={16} />
              <span>{settings.invite_link}</span>
            </div>
            <div className="invite-actions">
              <button className="btn btn-ghost btn-sm" onClick={copyInviteLink}>
                <Copy size={16} />
              </button>
              <a 
                href={settings.invite_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-ghost btn-sm"
              >
                <ExternalLink size={16} />
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="settings-layout">
        {/* Tabs */}
        <div className="settings-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="settings-section">
              <h2 className="section-title">Основные настройки</h2>

              <div className="input-group">
                <label className="input-label">ID группы</label>
                <input
                  type="text"
                  className="input"
                  value={settings.group_id}
                  onChange={(e) => setSettings({...settings, group_id: e.target.value})}
                  placeholder="-1001234567890"
                />
              </div>

              <div className="input-group">
                <label className="input-label">Username группы</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">@</span>
                  <input
                    type="text"
                    className="input"
                    value={settings.group_username}
                    onChange={(e) => setSettings({...settings, group_username: e.target.value})}
                    placeholder="mygroup"
                  />
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">Название группы</label>
                <input
                  type="text"
                  className="input"
                  value={settings.group_title}
                  onChange={(e) => setSettings({...settings, group_title: e.target.value})}
                  placeholder="Моя группа"
                />
              </div>

              <div className="input-group">
                <label className="input-label">Описание</label>
                <textarea
                  className="input textarea"
                  value={settings.group_description}
                  onChange={(e) => setSettings({...settings, group_description: e.target.value})}
                  placeholder="Описание группы..."
                  rows={3}
                />
              </div>

              <div className="input-group">
                <label className="input-label">Пригласительная ссылка</label>
                <input
                  type="text"
                  className="input"
                  value={settings.invite_link}
                  onChange={(e) => setSettings({...settings, invite_link: e.target.value})}
                  placeholder="https://t.me/+ABC123..."
                />
              </div>

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.auto_approve}
                    onChange={(e) => setSettings({...settings, auto_approve: e.target.checked})}
                  />
                  <span className="toggle-switch"></span>
                  <div className="toggle-text">
                    <span className="toggle-title">Автоматическое одобрение</span>
                    <span className="toggle-desc">Автоматически принимать заявки на вступление</span>
                  </div>
                </label>
              </div>

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.notifications_enabled}
                    onChange={(e) => setSettings({...settings, notifications_enabled: e.target.checked})}
                  />
                  <span className="toggle-switch"></span>
                  <div className="toggle-text">
                    <span className="toggle-title">Уведомления</span>
                    <span className="toggle-desc">Получать уведомления о событиях в группе</span>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Moderation Tab */}
          {activeTab === 'moderation' && (
            <div className="settings-section">
              <h2 className="section-title">Модерация</h2>

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.anti_spam}
                    onChange={(e) => setSettings({...settings, anti_spam: e.target.checked})}
                  />
                  <span className="toggle-switch"></span>
                  <div className="toggle-text">
                    <span className="toggle-title">Антиспам</span>
                    <span className="toggle-desc">Автоматическое удаление спам-сообщений</span>
                  </div>
                </label>
              </div>

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.anti_flood}
                    onChange={(e) => setSettings({...settings, anti_flood: e.target.checked})}
                  />
                  <span className="toggle-switch"></span>
                  <div className="toggle-text">
                    <span className="toggle-title">Антифлуд</span>
                    <span className="toggle-desc">Ограничение частоты сообщений</span>
                  </div>
                </label>
              </div>

              {settings.anti_flood && (
                <div className="flood-settings">
                  <div className="input-group">
                    <label className="input-label">Лимит сообщений</label>
                    <input
                      type="number"
                      className="input"
                      value={settings.flood_limit}
                      onChange={(e) => setSettings({...settings, flood_limit: parseInt(e.target.value)})}
                      min={1}
                      max={20}
                    />
                    <span className="input-hint">Максимум сообщений за период</span>
                  </div>

                  <div className="input-group">
                    <label className="input-label">Период (секунды)</label>
                    <input
                      type="number"
                      className="input"
                      value={settings.flood_timeout}
                      onChange={(e) => setSettings({...settings, flood_timeout: parseInt(e.target.value)})}
                      min={10}
                      max={300}
                    />
                    <span className="input-hint">Время блокировки при превышении</span>
                  </div>
                </div>
              )}

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.rules_enabled}
                    onChange={(e) => setSettings({...settings, rules_enabled: e.target.checked})}
                  />
                  <span className="toggle-switch"></span>
                  <div className="toggle-text">
                    <span className="toggle-title">Правила группы</span>
                    <span className="toggle-desc">Показывать правила по команде /rules</span>
                  </div>
                </label>
              </div>

              {settings.rules_enabled && (
                <div className="input-group">
                  <label className="input-label">Текст правил</label>
                  <textarea
                    className="input textarea"
                    value={settings.rules_text}
                    onChange={(e) => setSettings({...settings, rules_text: e.target.value})}
                    placeholder="1. Уважайте других участников&#10;2. Не спамьте&#10;3. ..."
                    rows={6}
                  />
                </div>
              )}
            </div>
          )}

          {/* Welcome Tab */}
          {activeTab === 'welcome' && (
            <div className="settings-section">
              <h2 className="section-title">Приветственное сообщение</h2>

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.welcome_enabled}
                    onChange={(e) => setSettings({...settings, welcome_enabled: e.target.checked})}
                  />
                  <span className="toggle-switch"></span>
                  <div className="toggle-text">
                    <span className="toggle-title">Приветствие включено</span>
                    <span className="toggle-desc">Отправлять сообщение новым участникам</span>
                  </div>
                </label>
              </div>

              {settings.welcome_enabled && (
                <>
                  <div className="input-group">
                    <label className="input-label">Текст приветствия</label>
                    <textarea
                      className="input textarea"
                      value={settings.welcome_message}
                      onChange={(e) => setSettings({...settings, welcome_message: e.target.value})}
                      placeholder="Добро пожаловать в нашу группу, {name}! 👋"
                      rows={5}
                    />
                    <span className="input-hint">
                      Доступные переменные: {'{name}'}, {'{username}'}, {'{group}'}
                    </span>
                  </div>

                  <div className="preview-card">
                    <h4>Предпросмотр</h4>
                    <div className="preview-message">
                      {settings.welcome_message
                        .replace('{name}', 'Иван')
                        .replace('{username}', '@ivan123')
                        .replace('{group}', settings.group_title || 'Группа')
                        || 'Текст приветствия не задан'}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Group;
